// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-engine — BASE / $INSBASE insertion-base mutation
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's BASE command (RU alias БАЗА) sets the drawing's insertion base
// point — the DXF HEADER `$INSBASE` system variable (group codes 10/20/30).
// This is the origin AutoCAD uses when the drawing is later inserted as a
// block into another drawing. BASE does NOT move any geometry; it only
// relocates the reference origin recorded in the header.
//
// Per CLAUDE.md "любые изменения геометрии/модели только через Command Engine":
// the document-level `insertionBase` field is mutated exclusively through this
// helper. The inverse (undo) is produced by `computeInverse` in
// geometryMutations.ts, which snapshots the prior base from the pre-mutation
// document — so undo restores the previous point as a single atomic step.
// ═══════════════════════════════════════════════════════════════════════════

import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

/** A 3D point payload for the insertion base (Z optional → defaults to 0). */
export interface InsertionBasePointInput {
  x: number;
  y: number;
  z?: number;
}

/**
 * Return a new {@link DrawingDocument} whose `insertionBase` field is set to
 * the given point ($INSBASE). Pure / immutable — the input document is not
 * mutated. Z defaults to 0 when omitted (AutoCAD treats BASE as 3D but
 * 2D-only callers commonly supply only X/Y).
 *
 * The result is a shallow spread; callers that need a fully re-validated
 * document (the geometry reducer) wrap this in `assembleDrawingDocument`.
 */
export function applySetInsertionBase(
  document: DrawingDocument,
  point: InsertionBasePointInput,
): DrawingDocument {
  return {
    ...document,
    insertionBase: { x: point.x, y: point.y, z: point.z ?? 0 },
  };
}
